import React from "react";
import { fetchAdminAllTenants, fetchInstructorSignups, fetchPendingRegistrationApprovals } from "../../../api/apiClient";
import { getAuditLog } from "../../../api/globalAdminApi";
import { Num } from "./adminCharts";
import useAsync from "../widgets/useAsync";
import { LoadFailed, Loading } from "../widgets/commonBodies";
import { ROUTES } from "../shared/classData";

const countPendingApplicants = (groups) => (Array.isArray(groups) ? groups : []).reduce(
  (n, g) => n + (g.applicants || []).filter((a) => a.status === "PENDING").length, 0);

/** Everything waiting on an admin decision: enrollments, instructor sign-ups and course submissions. */
export function ApprovalsBody({ ctx }) {
  const { loading, data, error } = useAsync(async () => {
    const [enrollments, signups] = await Promise.all([
      fetchPendingRegistrationApprovals().catch(() => []),
      fetchInstructorSignups(ctx.tenantId).catch(() => []),
    ]);
    return { enrollments: (enrollments || []).length, signups: countPendingApplicants(signups) };
  }, [ctx.tenantId]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="approvals" />;
  const submissions = ctx.raw.submissions.filter((s) => String(s.status || "").toLowerCase() === "pending").length;
  const rows = [
    { key: "enroll", n: data.enrollments, label: "Enrollment requests", detail: "Learners waiting to join a class", cta: "Review", to: ROUTES.approvals },
    { key: "signup", n: data.signups, label: "Instructor sign-ups", detail: "Instructors who asked to teach a session", cta: "Review", to: ROUTES.approvals },
    { key: "submit", n: submissions, label: "Course submissions", detail: "New courses awaiting approval", cta: "Review", to: ROUTES.approvals },
  ];
  return (
    <>
      <div className="wst">{rows.map((r) => <div key={r.key}><b style={r.n ? { color: "var(--color-warn)" } : undefined}><Num to={r.n} /></b><span>{r.label}</span></div>)}</div>
      <div className="wl">{rows.filter((r) => r.n > 0).map((r) => (
        <div key={r.key} className="wr"><div><b>{r.n} {r.label.toLowerCase()}</b><small>{r.detail}</small></div>
          <button type="button" className="go" onClick={() => ctx.navigate(r.to)}>{r.cta}</button></div>))}
      {rows.every((r) => !r.n) && <p className="wg-empty">Nothing is waiting on an approval.</p>}</div>
    </>
  );
}

/** Global admin: each training center, its custom domain and whether it is locked. */
export function TenantsBody() {
  const { loading, data, error } = useAsync(() => fetchAdminAllTenants(), []);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="training centers" />;
  const rows = (data || []).map((t) => ({ id: t.id, name: t.name, domain: t.config?.customDomain || "", locked: Boolean(t.locked) }));
  if (!rows.length) return <p className="wg-empty">No training centers yet.</p>;
  const kind = (r) => (r.locked ? ["bad", "Locked"] : r.domain ? ["ok", "Live"] : ["warn", "No domain"]);
  return (
    <table className="wtb"><thead><tr><th>Training center</th><th className="hm">Domain</th><th>Status</th></tr></thead>
      <tbody>{rows.slice(0, 8).map((r) => { const [k, label] = kind(r); return (
        <tr key={r.id}><td>{r.name}</td><td className="hm mo" style={{ opacity: r.domain ? 0.8 : 0.5 }}>{r.domain || "No domain mapped"}</td><td><span className={`tag ${k}`}>{label}</span></td></tr>); })}</tbody></table>
  );
}

/** Global admin: the latest audit-log events. */
export function ActivityBody() {
  const { loading, data, error } = useAsync(() => getAuditLog({ days: 1, page: 0, size: 6 }), []);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="the audit log" />;
  const entries = data?.entries || [];
  if (!entries.length) return <p className="wg-empty">No activity in the last day.</p>;
  return (
    <div className="wl">{entries.map((e) => (
      <div key={e.id} className="tlog"><span className="mo">{String(e.tsAbsolute || e.tsDate || "").slice(-5)}</span>
        <div><b style={{ fontWeight: 500 }}>{e.actionLabel || e.action}</b><small>{[e.actor?.name, e.description].filter(Boolean).join(" · ")}</small></div></div>))}</div>
  );
}
